/**
 * Geopunt Vlaanderen HISTCART historical-map overlays.
 *
 * All layers come from the same WMTS endpoint with only the LAYER param
 * changing. The endpoint is KVP-style WMTS but uses TileMatrixSet `GoogleMapsVL`
 * which is Web Mercator — so MapLibre's `tiles` array works directly with
 * `TILEMATRIX={z}&TILECOL={x}&TILEROW={y}` substitution.
 *
 * License: Gratis Open Data Licentie Vlaanderen v1.2 (commercial OK,
 * attribution required to "Digitaal Vlaanderen" with the source year).
 *
 * Reference: D8 in decision-log.md (live-confirmed at HISTCART GetCapabilities).
 */

export type HistoricalOverlayId =
  | 'pourbus'
  | 'fricx'
  | 'ferraris'
  | 'buurtwegen'
  | 'vandermaelen'
  | 'popp';

export interface HistoricalOverlay {
  id: HistoricalOverlayId;
  /** Short display name shown in the layer panel. */
  name: string;
  /** Subtitle shown under the name (e.g. "1777"). */
  year: string;
  /** Long display name used in attribution lines. */
  attributionTitle: string;
  /** WMTS layer name on the HISTCART service. */
  layerName: string;
  /** Optional one-line note about geographic coverage (some are sub-region only). */
  coverageNote?: string;
}

export const HISTORICAL_OVERLAYS: HistoricalOverlay[] = [
  {
    id: 'pourbus',
    name: 'Pourbus',
    year: '1571',
    attributionTitle: 'Pourbuskaart (1571)',
    layerName: 'pourbus',
    // Pieter Pourbus's hand-painted survey commissioned by the Brugse Vrije
    // (the rural châtellenie around Bruges). The georeferenced tile is mostly
    // blank outside that region — only the Bruges hinterland is mapped.
    coverageNote: 'Brugge + Brugse Vrije (Knokke, Damme, Oostkamp, Maldegem area)',
  },
  {
    id: 'fricx',
    name: 'Fricx',
    year: '1712',
    attributionTitle: 'Fricx-kaart (1712)',
    // GetCapabilities exposes this layer as `frickx` (k-x), not `fricx`.
    // Get this name wrong and every tile request silently returns HTTP 400.
    layerName: 'frickx',
    // 24-sheet topographic map of the Spanish Netherlands by Eugène Henri
    // Fricx (Brussels engraver, 1712). Geopunt tiles render the Flemish
    // portion. Coarser than Ferraris but the only pre-Austrian-Netherlands
    // overview map we have.
    coverageNote: 'All of Flanders (cropped from a Spanish-Netherlands-wide map)',
  },
  {
    id: 'ferraris',
    name: 'Ferraris',
    year: '1777',
    attributionTitle: 'Ferrariskaart (1777)',
    layerName: 'ferraris',
    // Joseph de Ferraris's Kabinetskaart der Oostenrijkse Nederlanden — the
    // first scientific topo map of the region, KBR copy.
    coverageNote: 'All of Flanders, ~1:11,520',
  },
  {
    id: 'buurtwegen',
    name: 'Atlas der Buurtwegen',
    year: '1841',
    attributionTitle: 'Atlas der Buurtwegen (ca. 1841)',
    layerName: 'abw',
    // Per-municipality road atlas commissioned by the 1841 Belgian law on
    // local roads (vicinal roads). Five Flemish provinces, parcel-level detail.
    coverageNote: 'All Flemish municipalities, parcel-level, 1:5,000',
  },
  {
    id: 'vandermaelen',
    name: 'Vandermaelen',
    year: '1846–1854',
    attributionTitle: 'Vandermaelen (1846–1854)',
    layerName: 'vandermaelen',
    // Philippe Vandermaelen's Cartes topographiques de la Belgique, 1:20,000
    // — the first national topographic series, surveyed mid-19th century.
    coverageNote: 'All of Flanders, 1:20,000',
  },
  {
    id: 'popp',
    name: 'Popp',
    year: '1842–1879',
    attributionTitle: 'Popp (1842–1879)',
    layerName: 'popp',
    // P.C. Popp's Atlas cadastral parcellaire de la Belgique — pure parcel
    // boundaries, useful for tracing 19th-c. property history block-by-block.
    coverageNote: 'All of Flanders, cadastral parcels per municipality',
  },
];

const HISTCART_BASE = 'https://geo.api.vlaanderen.be/HISTCART/wmts';

/**
 * Build a MapLibre-compatible tile URL template for a given HISTCART layer.
 * The `{z}/{x}/{y}` placeholders are MapLibre's; the API is KVP WMTS so the
 * actual query string maps z→TILEMATRIX, x→TILECOL, y→TILEROW.
 */
export function tileUrlForOverlay(layerName: string): string {
  const params = new URLSearchParams({
    SERVICE: 'WMTS',
    VERSION: '1.0.0',
    REQUEST: 'GetTile',
    LAYER: layerName,
    STYLE: '',
    FORMAT: 'image/png',
    TILEMATRIXSET: 'GoogleMapsVL',
    // Placeholders — MapLibre substitutes these per tile request. URLSearchParams
    // would percent-encode the curly braces, so we append manually.
  });
  return `${HISTCART_BASE}?${params.toString()}&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}`;
}

export const HISTCART_ATTRIBUTION_LINK =
  '<a href="https://www.geopunt.be/" target="_blank" rel="noopener noreferrer">Digitaal Vlaanderen</a>';

/** Default state when the user has never toggled an overlay. */
export const DEFAULT_OVERLAY_STATE: Record<HistoricalOverlayId, OverlayState> = {
  pourbus: { visible: false, opacity: 0.7 },
  fricx: { visible: false, opacity: 0.7 },
  ferraris: { visible: true, opacity: 1.0 },
  buurtwegen: { visible: false, opacity: 0.7 },
  vandermaelen: { visible: false, opacity: 0.7 },
  popp: { visible: false, opacity: 0.7 },
};

export interface OverlayState {
  visible: boolean;
  /** 0.0 (transparent) — 1.0 (opaque). */
  opacity: number;
}

export type OverlayStates = Record<HistoricalOverlayId, OverlayState>;

const STORAGE_KEY = 'maplens.overlays.v2';

/** Read overlay state from localStorage. Returns defaults on miss / parse error. */
export function loadOverlayState(): OverlayStates {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { ...DEFAULT_OVERLAY_STATE };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_OVERLAY_STATE };
    const parsed = JSON.parse(raw) as Partial<OverlayStates>;
    // Merge over defaults so layers added in later versions get the new defaults
    // without wiping the user's existing toggles for older layers.
    const merged: OverlayStates = { ...DEFAULT_OVERLAY_STATE };
    for (const id of Object.keys(DEFAULT_OVERLAY_STATE) as HistoricalOverlayId[]) {
      const sanitised = sanitiseEntry(parsed[id]);
      if (sanitised !== null) merged[id] = sanitised;
    }
    return merged;
  } catch {
    return { ...DEFAULT_OVERLAY_STATE };
  }
}

/** Persist overlay state to localStorage. Silently no-ops if storage is unavailable. */
export function saveOverlayState(state: OverlayStates): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // QuotaExceeded or storage disabled — overlay state simply doesn't persist.
  }
}

function sanitiseEntry(entry: OverlayState | undefined): OverlayState | null {
  if (entry === undefined || entry === null) return null;
  if (typeof entry.visible !== 'boolean') return null;
  if (typeof entry.opacity !== 'number' || entry.opacity < 0 || entry.opacity > 1) return null;
  return { visible: entry.visible, opacity: entry.opacity };
}
